import Grid from "@mui/material/Grid";
import { useMemo, useState } from "react";

import Box from "../Box";
import Button from "../Button";
import DataTable from "../DataTable";
import Typography from "../Typography";
import Helpers from "../../commons/helpers";
import useStrings from "../../hooks/useStrings";
import CustomStatusCell from "../CustomStatusCell";
import useContainerGroup from "../../hooks/useContainerGroup";
import ModalCreateUpdateGroup from "../Group/modalCreateUpdateGroup";

import { Mode, Status } from "../../constants/enums";
import { Constants } from "../../constants/constants";
import { IDataDetailGroup } from "../../containers/group/edit";
import { IOrgProfile } from "../../hooks/useContainerOrganization";
import { ICodename, ILableOrganization } from "../../commons/interfaces";
import { useCommonComponentContext } from "../../providers/CommonComponentProvider";



interface IGroupStepProps {
    mode: number;
    organizationId: string;
    groupIdDefault?: string;
    dataRowByGroup: IOrgProfile[];

    hidenActionDelete: boolean;
    hidenActionUpdate: boolean;
    hidenActionCreate: boolean;
    hidenInputSelectGroupParent?: boolean;
    requiredInputSelectGroupParent?: boolean;

    onCallBack: () => void;
}

const GroupStep = (props: IGroupStepProps & ILableOrganization) => {
    const strings = useStrings();

    const { handleChangeStatusGroup, handleDeleteGroup } = useContainerGroup();

    const {
        language,
    } = useCommonComponentContext();

    const newLableGroup = useMemo(() => (
        (props.lableGroup || strings.Common.GROUP).toLowerCase()
    ), [props.lableGroup, strings]);

    const [dataModalGroup, setDataModalGroup] = useState<{
        open: boolean,
        mode: number,
        data?: IDataDetailGroup,
    }>({
        open: false,
        mode: Mode.View,
    });

    const listParentGroups: ICodename[] = useMemo(() => {
        const newData: ICodename[] = [];
        props.dataRowByGroup.forEach(item => {
            if (dataModalGroup.data?.id !== item.groupId) {
                newData.push({
                    code: item.groupId,
                    name: item.name?.value?.[language],
                    detail: {
                        type: item.type,
                        parentGroup: item.parentGroup,
                    }
                });
            }
        });
        return newData;
    }, [props.dataRowByGroup, language, dataModalGroup.data?.id]);

    const dataColumns = useMemo(() => {
        const newItems = [
            {
                Header: Helpers.capitalizeFirstLetter(strings.format(strings.Group.NAME_GROUP, newLableGroup)), accessor: "name",
                Cell: (row: any) => (
                    <Typography variant="caption" fontWeight="medium" color="text">
                        {Helpers.renderValueByLanguage({ data: row.value, currentLanguage: language })}
                    </Typography>
                )
            },
            {
                Header: strings.Common.STATUS, accessor: "groupStatus",
                Cell: (row: any) => (<CustomStatusCell status={Number(row.value)} />),
            },
        ];

        if (!props.hidenInputSelectGroupParent) {
            const itemTemp = [
                {
                    Header: Helpers.capitalizeFirstLetter(strings.format(strings.Group.GROUP_PARENT, newLableGroup)), accessor: "parentGroupName",
                    Cell: (row: any) => (
                        <Typography variant="caption" fontWeight="medium" color="text">
                            {(props.groupIdDefault === row.row?.original?.parentGroup)
                                ? ""
                                : row.value?.value?.[language]
                            }
                        </Typography>
                    )
                },
                {
                    Header: Helpers.capitalizeFirstLetter(strings.format(strings.Group.CHILD_NUMBER, newLableGroup)), accessor: "childNumber",
                    Cell: (row: any) => (
                        <Typography variant="caption" fontWeight="medium" color="text">
                            {row.value}
                        </Typography>
                    )
                },
            ];

            newItems.splice(1, 0, ...itemTemp);

        } else {
            const itemTemp = [
                {
                    Header: strings.Organization.DESCRIPTION, accessor: "description",
                    Cell: (row: any) => (
                        <Typography variant="caption" fontWeight="medium" color="text">
                            {row.value?.value?.[language] || ""}
                        </Typography>
                    )
                },
            ];

            newItems.splice(1, 0, ...itemTemp);
        }

        return newItems;

    }, [props.hidenInputSelectGroupParent]);

    return (
        <Grid item xs={12}>
            <Box p={2}>
                <Box mb={2} display="flex" alignItems="center" justifyContent="space-between">
                    <Typography variant="h5">
                        {strings.format(strings.Staff.TITLE_LIST_VIEW, newLableGroup)}
                    </Typography>
                    {
                        (props.mode !== Mode.View) && !props.hidenActionCreate &&
                        <>
                            <Button
                                onClick={() => {
                                    setDataModalGroup({ open: true, mode: Mode.Create });
                                }}
                            >
                                {`${strings.Common.ADD_NEW} ${newLableGroup}`}
                            </Button>
                            <ModalCreateUpdateGroup
                                lable={newLableGroup}
                                mode={dataModalGroup.mode}
                                openModal={dataModalGroup.open}
                                listParentGroup={listParentGroups}
                                dataDetailGroup={dataModalGroup.data}
                                organizationId={props.organizationId}
                                groupIdDefault={props.groupIdDefault}
                                hidenInputSelectGroupParent={props.hidenInputSelectGroupParent}
                                requiredInputSelectGroupParent={props.requiredInputSelectGroupParent}

                                onCloseModal={() => {
                                    setDataModalGroup({ open: false, mode: Mode.View });
                                }}
                                onActionModal={(data) => {
                                    setDataModalGroup({ open: false, mode: Mode.View });

                                    props.onCallBack();
                                }}
                            />
                        </>
                    }
                </Box>

                <DataTable
                    isLocal
                    pageNumber={1}
                    rowPerPage={Constants.ROW_PER_PAGE}
                    totalCount={props.dataRowByGroup.length}
                    hideActionMenu={props.mode === Mode.View}

                    table={{
                        columns: dataColumns,
                        rows: props.dataRowByGroup || []
                    }}

                    actionList={(row) => [
                        !props.hidenActionUpdate &&
                        {
                            key: "edit",
                            iconName: "edit",
                            title: strings.Common.EDIT,
                            callback: (row) => {
                                const parentGroupId = (props.groupIdDefault === row.parentGroup) ? undefined : row.parentGroup;
                                setDataModalGroup({
                                    open: true,
                                    mode: Mode.Update,
                                    data: {
                                        id: row.groupId,
                                        name: row.name,
                                        description: row.description,
                                        parentGroupId: parentGroupId,
                                        status: row.groupStatus,
                                    }
                                });
                            },
                        },
                        !props.hidenActionDelete &&
                        {
                            key: "delete",
                            iconName: "delete",
                            title: strings.Common.DELETE,
                            callback: (row) => {
                                handleDeleteGroup({
                                    lable: newLableGroup,
                                    onCallBack: () => {
                                        props.onCallBack();
                                    },
                                    params: {
                                        id: row.groupId,
                                        childNumber: Number(row.childNumber),
                                    },
                                })
                            },
                        },
                        !props.hidenActionUpdate &&
                        {
                            key: "inactive",
                            iconName: (Number(row.groupStatus) === Status.Active) ? "close" : "check",
                            title: (Number(row.groupStatus) === Status.Active) ? strings.Common.IN_ACTIVE : strings.Common.ACTIVE,
                            callback: (row) => {
                                handleChangeStatusGroup({
                                    onCallBack: () => {
                                        props.onCallBack();
                                    },
                                    params: {
                                        id: row.groupId,
                                        status: Number(row.groupStatus),
                                        organizationId: props.organizationId,
                                    },
                                });
                            },
                        },
                    ]}
                />
            </Box>
        </Grid>
    );
};

export default GroupStep;
